import { motion, animate } from "framer-motion";
import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import type { Stats } from "../index";

interface CounterProps {
    target: number;
    suffix?: string;
}

function AnimatedCounter({ target, suffix = "" }: CounterProps) {
    const [value, setValue] = useState(0);

    useEffect(() => {
        const controls = animate(0, target, {
            duration: 2,
            ease: "easeOut",
            onUpdate: (v) => setValue(Math.floor(v)),
        });
        return controls.stop;
    }, [target]);

    return (
        <span>
            {value.toLocaleString("pt-AO")}{suffix}
        </span>
    );
}

interface Props {
    stats: Stats;
}

export default function SocialProofSection({ stats }: Props) {
    const STATS = [
        {
            icon: "mdi:domain",
            value: stats.empresas,
            suffix: "+",
            label: "Empresas registadas",
            color: "text-blue-500",
        },
        {
            icon: "mdi:receipt-text-check-outline",
            value: stats.facturas,
            suffix: "+",
            label: "Facturas emitidas",
            color: "text-blue-500",
        },
        {
            icon: "mdi:account-group",
            value: stats.utilizadores,
            suffix: "+",
            label: "Utilizadores activos",
            color: "text-purple-500",
        },
        {
            icon: "mdi:store-outline",
            value: stats.estabelecimentos,
            suffix: "+",
            label: "Estabelecimentos",
            color: "text-amber-500",
        },
    ];

    return (
        <section className="py-16 bg-white border-y border-gray-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                    {STATS.map((stat, index) => (
                        <motion.div
                            key={stat.label}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            className="text-center"
                        >
                            <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gray-50 mb-3 ${stat.color}`}>
                                <Icon icon={stat.icon} width={24} />
                            </div>
                            <div className="text-3xl sm:text-4xl font-bold text-gray-900 mb-1">
                                <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                            </div>
                            <div className="text-sm text-gray-500">{stat.label}</div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
